import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DemandStatut, DemandStatusUpdateDTO } from '../models/demand-status';
import { AuthenticationService } from './authentication.service';
import { CurrentUserService } from './current-user.service';
import { AuditLog } from '../models/AuditLog';
import { UserProfile } from '../models/user-profile';
import { Echeance } from '../models/echeance';
import { EcheanceStatus } from '../models/echeance-status';

@Injectable({ providedIn: 'root' })
export class DemandStatusService {

  userProfile: string | null = null;

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private currentUserService: CurrentUserService
  ) {}

  // ── Headers audit ─────────────────────────────────────────────────────────

  private createAuditHeaders(): HttpHeaders {
    const currentUser = this.currentUserService.getCurrentUser() as any || {};

    const requestId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const role = currentUser?.role ?? '';
    const displayName = currentUser.displayName
      ?? `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim();

    this.userProfile = role;

    return new HttpHeaders({
      'Content-Type':               'application/json',
      'Access-Control-Allow-Origin': '*',
      'X-User-Identifier':          currentUser.identifier ?? '',
      'X-User-Display-Name':        displayName,
      'X-User-Role':                role,
      'X-Actor-Source':             'FE',
      'X-Request-ID':               requestId
    });
  }

  private get options() {
    return { headers: this.createAuditHeaders() };
  }

  // ── API ───────────────────────────────────────────────────────────────────

  updateStatus(demandId: string, dto: DemandStatusUpdateDTO): Observable<any> {
    return this.http.put(
      `${environment.backendApiUrl}/status/demand/${demandId}`,
      dto,
      this.options
    );
  }

  getAuditHistory(entityType: string, entityId: string): Observable<AuditLog[]> {
    return this.http.get<AuditLog[]>(
      `${environment.backendApiUrl}/audit/${entityType}/${entityId}`,
      this.options
    );
  }

  // ── Transitions possibles ─────────────────────────────────────────────────

  /**
   * Retourne les transitions métier autorisées depuis le statut actuel.
   * Les règles liées aux échéances sont vérifiées séparément dans
   * filterTransitionsByRole (côté composant) et dans DemandWorkflow (backend).
   */
  getTransitionsPossibles(
    statutActuel: DemandStatut,
    historique: AuditLog[]
  ): DemandStatut[] {

    if (this.userProfile === UserProfile.ADMIN) {
      return Object.values(DemandStatut).filter(s => s !== statutActuel);
    }

    const aEteEngage = historique.some(log => {
      const snapshot = typeof log.snapshotAfter === 'string'
        ? JSON.parse(log.snapshotAfter)
        : log.snapshotAfter;
      const statut = snapshot?.state;
      if (!statut) return false;
      return this.fromBackendStatut(statut) === DemandStatut.ENGAGE;
    });

    const transitions: Record<DemandStatut, DemandStatut[]> = {
      [DemandStatut.CHIFFRE]:    [DemandStatut.ENGAGE, DemandStatut.EN_ATTENTE, DemandStatut.ANNULE],
      [DemandStatut.ENGAGE]:     [DemandStatut.EN_ATTENTE, DemandStatut.LIVRE],
      [DemandStatut.EN_ATTENTE]: aEteEngage
        ? [DemandStatut.ENGAGE]
        : [DemandStatut.CHIFFRE, DemandStatut.ENGAGE],
      [DemandStatut.LIVRE]:      [DemandStatut.FERME],
      [DemandStatut.FERME]:      [],
      [DemandStatut.ANNULE]:     []
    };

    return transitions[statutActuel] ?? [];
  }

  // ── Règles métier sur les échéances ───────────────────────────────────────

  /**
   * Règle 1 — ENGAGE → EN_ATTENTE :
   * Bloqué si la demande a au moins une échéance.
   */
  peutRevenirEnAttente(echeances: Echeance[]): boolean {
    return !echeances || echeances.length === 0;
  }

  /**
   * Règle 2 — ENGAGE → LIVRE :
   * Autorisé uniquement si TOUTES les échéances sont :
   *   - non projetées (projected === false)
   *   - statut === VALIDE_SNCF
   */
  peutPasserALivre(echeances: Echeance[]): boolean {
    if (!echeances || echeances.length === 0) return false;
    return echeances.every(e =>
      e.projected === false
    );
  }

  /**
   * Applique les règles échéances sur une transition donnée.
   * Retourne null si la transition est autorisée,
   * ou un message d'erreur si elle est bloquée.
   */
  validerTransitionEcheances(
    currentStatut: DemandStatut,
    nextStatut: DemandStatut,
    echeances: Echeance[]
  ): string | null {

    // Règle 1 : ENGAGE → EN_ATTENTE bloqué si échéances présentes
    if (currentStatut === DemandStatut.ENGAGE
        && nextStatut === DemandStatut.EN_ATTENTE) {
      if (!this.peutRevenirEnAttente(echeances)) {
        return 'Impossible de revenir en attente : la demande possède des échéances en cours.';
      }
    }

    // Règle 2 : ENGAGE → LIVRE uniquement si toutes les échéances validées SNCF
    if (currentStatut === DemandStatut.ENGAGE
        && nextStatut === DemandStatut.LIVRE) {
      if (!this.peutPasserALivre(echeances)) {
        return 'Impossible de livrer : toutes les échéances doivent être facturées.';
      }
    }

    return null; // transition autorisée
  }

  // ── Labels / couleurs ─────────────────────────────────────────────────────

  getStatutLabel(statut: DemandStatut): string {
    const labels: Record<DemandStatut, string> = {
      [DemandStatut.EN_ATTENTE]: 'En attente',
      [DemandStatut.CHIFFRE]:    'Chiffré',
      [DemandStatut.ENGAGE]:     'Engagé',
      [DemandStatut.LIVRE]:      'Livré',
      [DemandStatut.FERME]:      'Fermé',
      [DemandStatut.ANNULE]:     'Annulé'
    };
    return labels[statut] ?? statut;
  }

  statusColor(statut: DemandStatut): string {
    switch (statut) {
      case DemandStatut.EN_ATTENTE:
      case DemandStatut.CHIFFRE:
      case DemandStatut.ENGAGE:
        return 'state-blue';
      case DemandStatut.ANNULE:
        return 'state-red';
      case DemandStatut.LIVRE:
      case DemandStatut.FERME:
        return 'state-green';
      default:
        return 'status-grey';
    }
  }

  fromBackendStatut(label: string): DemandStatut {
    const map: Record<string, DemandStatut> = {
      'En attente': DemandStatut.EN_ATTENTE,
      'Chiffré':    DemandStatut.CHIFFRE,
      'Engagé':     DemandStatut.ENGAGE,
      'Livré':      DemandStatut.LIVRE,
      'Fermé':      DemandStatut.FERME,
      'Annulé':     DemandStatut.ANNULE
    };
    return map[label] ?? DemandStatut.EN_ATTENTE;
  }
}